import { useContext, MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { ChevronDownIcon, ChevronUpIcon } from '@dynatrace/strato-icons';

import * as styles from './Stepper.sty.js';
import { Button } from '../../buttons/button/Button.js';
import { devLog } from '../../core/utils/logging.js';
import { Divider } from '../../layouts/divider/Divider.js';
import { Flex } from '../../layouts/flex/Flex.js';
import * as baseInputStyles from '../_base-input/BaseInput.sty.js';
import { BaseInputContext } from '../_base-input/contexts/BaseInputContext.js';

//#region Typings

/**
 * The properties of the SearchInput Stepper.
 * @public
 */
export type SearchInputStepperProps = {
  /** The zero-based index of the current match. */
  current: number;
  /** Called when the current match index should change. */
  onCurrentChange: (value: number) => void;
  /** The total number of matches. */
  total: number;
};

//#endregion Typings

export const Stepper = ({
  current,
  onCurrentChange,
  total,
}: SearchInputStepperProps) => {
  const intl = useIntl();
  const { hasError, disabled, ariaDisabled } = useContext(BaseInputContext);
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  if (current > total) {
    devLog(
      `The current match index (${current}) is greater than the total number of matches (${total}). Please ensure that the current index is zero-based and less than the total.`,
    );
  }

  const currentDisplay = total === 0 ? 0 : current + 1;

  return (
    <Flex gap={4}>
      <output
        className={styles.matchPositionIndicators}
        aria-live="polite"
        aria-label={intl.formatMessage(
          {
            id: 'tw9ulrSgegYXHq69',
            defaultMessage: 'Match {current} of {total}',
            description: 'Label for the current match position',
          },
          { current: currentDisplay, total },
        )}
      >
        <span>{currentDisplay}</span> / <span>{total}</span>
      </output>
      <Divider
        orientation="vertical"
        className={baseInputStyles.divider}
        aria-hidden
      />
      <div className={styles.stepperButtons} role="group">
        <Button
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onCurrentChange(current === 0 ? total - 1 : current - 1);
          }}
          className={styles.stepperButton}
          color={hasError ? 'critical' : 'neutral'}
          disabled={disabledOrAriaDisabled || total === 0}
          aria-label={intl.formatMessage({
            id: 'GLK6cN59tiRO9GDp',
            defaultMessage: 'Previous match',
            description: 'Label for the previous match button',
          })}
        >
          <Button.Prefix>
            <ChevronUpIcon />
          </Button.Prefix>
        </Button>
        <Button
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onCurrentChange(current === total - 1 ? 0 : current + 1);
          }}
          className={styles.stepperButton}
          color={hasError ? 'critical' : 'neutral'}
          disabled={disabledOrAriaDisabled || total === 0}
          aria-label={intl.formatMessage({
            id: 'JLMJNMOPi2dCAbD1',
            defaultMessage: 'Next match',
            description: 'Label for the next match button',
          })}
        >
          <Button.Prefix>
            <ChevronDownIcon />
          </Button.Prefix>
        </Button>
      </div>
    </Flex>
  );
};

(Stepper as typeof Stepper & { displayName: string }).displayName =
  'SearchInput.Stepper';
